'use client';

import { useState } from 'react';
import { cn } from '@/src/shared/lib/utils';

interface ToggleProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  /** Legacy: accepts React.ChangeEvent for backward compat with <input> pattern */
  onLegacyChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  error?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

function Toggle({
  checked,
  defaultChecked = false,
  onChange,
  onLegacyChange,
  disabled = false,
  label,
  error,
  className,
  id,
  'aria-label': ariaLabel,
}: ToggleProps) {
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = useState(defaultChecked);

  const isChecked = isControlled ? (checked as boolean) : internalChecked;

  const handleClick = () => {
    if (disabled) return;
    const next = !isChecked;
    if (!isControlled) setInternalChecked(next);
    onChange?.(next);
    // Legacy support: simulate ChangeEvent for callers using e.target.checked
    if (onLegacyChange) {
      onLegacyChange({ target: { checked: next } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-3">
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={isChecked}
          aria-label={ariaLabel ?? label}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none',
            isChecked
              ? disabled
                ? 'bg-accent-disabled'
                : 'bg-accent-primary'
              : disabled
                ? 'bg-text-tertiary opacity-50'
                : 'bg-text-tertiary',
            disabled && 'cursor-not-allowed'
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-md',
              isChecked ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
        {label && (
          <span
            className={cn(
              'text-mobile-body md:text-tablet-body lg:text-desktop-body select-none',
              disabled ? 'text-text-disabled' : 'text-text-primary'
            )}
          >
            {label}
          </span>
        )}
      </div>
      {error && (
        <p className="text-mobile-body-sm md:text-tablet-body-sm lg:text-desktop-body-sm text-feedback-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

Toggle.displayName = 'Toggle';

export { Toggle };
export type { ToggleProps };
